import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { doc, updateDoc, setDoc, deleteField } from "firebase/firestore";
import { db } from "../../../firebase";
import ImageManager from "./ImageManager";
import { logHistory } from "../HistoryDrawer";

const EditColor = "#0e76ec";
const DeleteColor = "#f13737";

export const getPreviewUrl = (path, id) => {
  if (!path || !id) return "/projects";
  const idLower = String(id).toLowerCase();
  if (idLower === "about") return "/about";
  if (idLower === "careers") return "/Careers";
  if (idLower === "contact") return "/Contact";
  if (idLower === "carousel" || idLower === "home") return "/";

  const segments = path.split("/").filter(Boolean);

  const projectsIndex = segments.lastIndexOf("PROJECTS");
  if (projectsIndex !== -1) {
    const afterProjects = segments.slice(projectsIndex + 1);
    const uniqueAfter = [];
    for (let i = 0; i < afterProjects.length; i++) {
      if (uniqueAfter.length === 0 || uniqueAfter[uniqueAfter.length - 1] !== afterProjects[i]) {
        uniqueAfter.push(afterProjects[i]);
      }
    }

    if (uniqueAfter.length === 0) {
      if (id === "PROJECTS" || id === "root" || id === "LWongWeb") {
        return "/projects";
      }
      return `/projects/${encodeURIComponent(id)}`;
    }

    if (uniqueAfter.length === 1) {
      const className = uniqueAfter[0];
      if (id === className) {
        return `/projects/${encodeURIComponent(className)}`;
      }
      return `/projects/${encodeURIComponent(className)}/${encodeURIComponent(id)}`;
    }

    if (uniqueAfter.length >= 2) {
      const className = uniqueAfter[0];
      const projectName = uniqueAfter[1];
      return `/projects/${encodeURIComponent(className)}/${encodeURIComponent(projectName)}`;
    }
  }

  if (id === "root" || id === "LWongWeb") return "/projects";
  return `/projects/${encodeURIComponent(id)}`;
};

export const getSummaryKey = (path, id) => {
  const segments = path.split("/").filter(Boolean);
  const rootIndex = segments.indexOf("root");
  if (rootIndex === -1) return `/${id}`;
  const afterRoot = segments.slice(rootIndex);
  const unique = [];
  for (let i = 0; i < afterRoot.length; i++) {
    if (unique.length === 0 || unique[unique.length - 1] !== afterRoot[i]) {
      unique.push(afterRoot[i]);
    }
  }
  if (!unique.includes(id)) {
    unique.push(id);
  }
  return "/" + unique.join("/");
};

let previewBc = null;
const getPreviewBc = () => {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!previewBc) {
    try {
      previewBc = new BroadcastChannel("lwong_live_preview");
    } catch (e) {}
  }
  return previewBc;
};

function EditTools(props) {
  const [data, setData] = useState({});
  const [adding, setAdding] = useState(false);
  const [fields, setFields] = useState([]);
  const [prevshow, setPrevShow] = useState(props.show);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const initialDataRef = useRef({});

  const uniqueKeyAddPropertyName = `${props.path}_${props.id}_add_property_name`;
  const uniqueKeyAddPropertyValue = `${props.path}_${props.id}_add_property_value`;
  const unique_class_input = `${props.path}_${props.id}_input`;
  const unique_class_name = `${props.path}_${props.id}_name`;

  useEffect(() => {
    if (props.info) {
      initialDataRef.current = { ...props.info };
    }
  }, [props.info]);

  // Instant live draft broadcast to preview iframe (0ms keystroke reflection)
  const broadcastLiveChange = (draftData) => {
    const summaryKey = getSummaryKey(props.path, props.id);
    const targetUrl = getPreviewUrl(props.path, props.id);
    const msg = {
      type: "LIVE_UPDATE",
      summaryKey,
      docData: draftData,
      previewUrl: targetUrl,
    };
    try {
      const bc = getPreviewBc();
      if (bc) bc.postMessage(msg);
    } catch (e) {}
    window.postMessage(msg, "*");
    document.querySelectorAll("iframe").forEach((f) => {
      try {
        f.contentWindow.postMessage(msg, "*");
      } catch (e) {}
    });
  };

  const pushData = async () => {
    if (data && Object.keys(data).length > 0) {
      try {
        const ref = doc(db, props.path, props.id);
        await updateDoc(ref, data);

        const summaryKey = getSummaryKey(props.path, props.id);
        const summaryRef = doc(db, "trees", "users", "bhzfBvdgxZYrvKvDaXz5iaQzIjn1", "summary");
        await setDoc(summaryRef, { [summaryKey]: data }, { merge: true });

        // Log history snapshot
        await logHistory({
          branchName: props.id,
          path: props.path,
          docId: props.id,
          user: "admin",
          action: "Edited fields",
          previousData: initialDataRef.current,
          newData: { ...initialDataRef.current, ...data },
          changedKeys: Object.keys(data),
        });

        initialDataRef.current = { ...initialDataRef.current, ...data };
        setData({});
        setHasUnsavedChanges(false);
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 2500);

        // Reset text colors back to normal
        const input_elements = document.getElementsByClassName(unique_class_input);
        for (let i = 0; i < input_elements.length; i++) {
          input_elements[i].style.color = "#1c1c1e";
        }
      } catch (err) {
        console.error("Error updating doc:", err);
      }
    }
  };

  const pushProperty = async () => {
    const add_title = document.getElementById(uniqueKeyAddPropertyName);
    const add_value = document.getElementById(uniqueKeyAddPropertyValue);
    if (add_title && add_value && add_title.value.trim() !== "") {
      const fieldName = add_title.value.trim();
      const fieldValue = add_value.value;
      try {
        const ref = doc(db, props.path, props.id);
        await setDoc(ref, { [fieldName]: fieldValue }, { merge: true });

        const summaryKey = getSummaryKey(props.path, props.id);
        const summaryRef = doc(db, "trees", "users", "bhzfBvdgxZYrvKvDaXz5iaQzIjn1", "summary");
        await setDoc(summaryRef, { [summaryKey]: { [fieldName]: fieldValue } }, { merge: true });

        await logHistory({
          branchName: props.id,
          path: props.path,
          docId: props.id,
          user: "admin",
          action: `Added field: ${fieldName}`,
          previousData: initialDataRef.current,
          newData: { ...initialDataRef.current, [fieldName]: fieldValue },
          changedKeys: [fieldName],
        });

        broadcastLiveChange({ [fieldName]: fieldValue });

        add_title.value = "";
        add_value.value = "";
        setAdding(false);
      } catch (err) {
        console.error("Error adding field:", err);
      }
    }
  };

  const deleteProperty = async (propKey) => {
    if (window.confirm(`Delete field "${propKey}"?`)) {
      try {
        const ref = doc(db, props.path, props.id);
        await updateDoc(ref, { [propKey]: deleteField() });

        const summaryKey = getSummaryKey(props.path, props.id);
        const summaryRef = doc(db, "trees", "users", "bhzfBvdgxZYrvKvDaXz5iaQzIjn1", "summary");
        await updateDoc(summaryRef, { [`${summaryKey}.${propKey}`]: deleteField() });

        const prevSnap = { ...initialDataRef.current };
        const newSnap = { ...initialDataRef.current };
        delete newSnap[propKey];

        await logHistory({
          branchName: props.id,
          path: props.path,
          docId: props.id,
          user: "admin",
          action: `Deleted field: ${propKey}`,
          previousData: prevSnap,
          newData: newSnap,
          changedKeys: [propKey],
        });

        broadcastLiveChange({ [propKey]: "" });
      } catch (err) {
        console.error("Error deleting field:", err);
      }
    }
  };

  // Keyboard shortcut: Cmd+S or Ctrl+S to save
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        if (props.show && hasUnsavedChanges) {
          e.preventDefault();
          pushData();
        }
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [props.show, hasUnsavedChanges, data]);

  // Auto-save when collapsing card
  useEffect(() => {
    if (prevshow !== props.show) {
      setPrevShow(props.show);
      if (!props.show && prevshow && hasUnsavedChanges) {
        pushData();
      }
    }
  }, [props.show, prevshow, hasUnsavedChanges]);

  const editableText = (key, value) => {
    if (typeof value === "string" || typeof value === "number") {
      const uniqueKeyInput = `${props.path}_${props.id}_${key}_input`;
      const uniqueKeyName = `${props.path}_${props.id}_${key}_name`;
      const strVal = String(value);

      if (key === "id") {
        return (
          <FieldRow key={uniqueKeyInput}>
            <FieldLabel id={uniqueKeyName}>{key} :</FieldLabel>
            <ReadOnlyBadge id={uniqueKeyInput}>{strVal}</ReadOnlyBadge>
          </FieldRow>
        );
      }

      const isLongText = strVal.length > 30 || strVal.includes("\n") || key.startsWith("text");

      const handleChange = (e) => {
        const val = e.target.value;
        const el = document.getElementById(uniqueKeyInput);
        if (el) {
          el.style.color = val === "" ? DeleteColor : EditColor;
        }
        setHasUnsavedChanges(true);
        const updated = {
          ...data,
          [key]: val,
        };
        if (key === "name") {
          updated.title1 = val;
        }
        setData(updated);
        // Instant live draft reflection on preview iframe
        broadcastLiveChange({ ...props.info, ...updated });
      };

      const handleFocus = () => {
        broadcastLiveChange({ ...props.info, ...data });
      };

      return (
        <FieldContainer key={uniqueKeyInput}>
          <LabelRow>
            <FieldLabel className={unique_class_name} id={uniqueKeyName}>
              {key} :
            </FieldLabel>
            {key !== "name" && key !== "id" && (
              <DeleteFieldButton
                type="button"
                onClick={() => deleteProperty(key)}
                title={`Delete field "${key}"`}
              >
                ✕
              </DeleteFieldButton>
            )}
          </LabelRow>

          {isLongText ? (
            <TextArea
              id={uniqueKeyInput}
              className={unique_class_input}
              defaultValue={strVal}
              onChange={handleChange}
              onFocus={handleFocus}
            />
          ) : (
            <TextInput
              id={uniqueKeyInput}
              className={unique_class_input}
              defaultValue={strVal}
              onChange={handleChange}
              onFocus={handleFocus}
            />
          )}
        </FieldContainer>
      );
    }
    return null;
  };

  useEffect(() => {
    if (props.info) {
      const array = [];
      let idEl = null;
      let nameEl = null;
      let imagesEl = null;

      const orderedInfo = Object.keys(props.info)
        .sort()
        .reduce((obj, k) => {
          obj[k] = props.info[k];
          return obj;
        }, {});

      for (const [key, value] of Object.entries(orderedInfo)) {
        if (key === "id") {
          idEl = editableText(key, value);
        } else if (key === "name") {
          nameEl = editableText(key, value);
        } else if (key === "images") {
          imagesEl = (
            <ImageManager
              key="image-manager"
              borderRadius={props.borderRadius}
              path={props.path}
              id={props.id}
              images={value}
              disabledImages={props.info?.disabledImages}
              projectName={props.info?.name || props.id}
              onImagesChanged={(newImages) => {
                broadcastLiveChange({ ...props.info, ...data, images: newImages });
              }}
            />
          );
        } else {
          array.push(editableText(key, value));
        }
      }

      setFields([
        idEl,
        nameEl,
        ...array,
        imagesEl,
      ]);
    }
  }, [props.info, props.id, props.path]);

  const previewUrl = getPreviewUrl(props.path, props.id);

  return (
    <Box style={{ opacity: props.show ? 1 : 0 }}>
      <InfoBox>
        <CardHeader>
          <HeaderLeft>
            <BranchTag title="Branch ID">{props.id}</BranchTag>
          </HeaderLeft>

          <HeaderRightActions>
            <SaveButton
              type="button"
              unsaved={hasUnsavedChanges}
              justSaved={justSaved}
              onClick={pushData}
              title={hasUnsavedChanges ? "Save changes to Firestore (Cmd+S)" : "No unsaved changes"}
            >
              {justSaved ? "✓ Saved" : hasUnsavedChanges ? "💾 Save *" : "💾 Save"}
            </SaveButton>

            <PreviewButton
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (props.onPreview) {
                  props.onPreview(previewUrl);
                }
              }}
              title={`Preview "${props.id}" on the live site`}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: "5px" }}
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              Preview
            </PreviewButton>
          </HeaderRightActions>
        </CardHeader>

        <FieldsList>{fields}</FieldsList>

        <AddFieldSection>
          {!adding ? (
            <ToggleAddButton type="button" onClick={() => setAdding(true)}>
              + Add Field
            </ToggleAddButton>
          ) : (
            <AddFieldCard>
              <AddFieldInputs>
                <AddInput
                  type="text"
                  placeholder="Field name (e.g. text4)"
                  id={uniqueKeyAddPropertyName}
                  autoFocus
                />
                <AddInput
                  type="text"
                  placeholder="Field value..."
                  id={uniqueKeyAddPropertyValue}
                />
              </AddFieldInputs>
              <AddActionRow>
                <SaveFieldButton type="button" onClick={pushProperty}>
                  Save Field
                </SaveFieldButton>
                <CancelFieldButton type="button" onClick={() => setAdding(false)}>
                  Cancel
                </CancelFieldButton>
              </AddActionRow>
            </AddFieldCard>
          )}
        </AddFieldSection>
      </InfoBox>
    </Box>
  );
}

export default EditTools;

const Box = styled.div`
  position: relative;
  color: #1c1c1e;
  height: 100%;
  transition: 0.2s;
`;

const InfoBox = styled.div`
  position: relative;
  box-sizing: border-box;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #e5e5ea;
  margin-bottom: 4px;
  gap: 8px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const HeaderRightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const BranchTag = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1c1c1e;
  letter-spacing: 0.3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.justSaved
      ? "#34c759"
      : props.unsaved
      ? "#0e76ec"
      : "#48484a"};
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
  letter-spacing: 0.2px;

  &:hover {
    opacity: 0.9;
    transform: scale(1.02);
  }

  &:active {
    transform: scale(0.98);
  }
`;



const PreviewButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #2a9d8e;
  color: white;
  border: none;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s;
  letter-spacing: 0.2px;

  &:hover {
    background-color: #238276;
    box-shadow: 0 2px 8px rgba(42, 157, 142, 0.35);
  }
`;

const FieldsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FieldLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #3a3a3c;
  letter-spacing: 0.2px;
`;

const DeleteFieldButton = styled.button`
  background: none;
  border: none;
  color: #8e8e93;
  font-size: 13px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  transition: 0.15s;

  &:hover {
    color: #e63946;
  }
`;

const ReadOnlyBadge = styled.div`
  background-color: #f2f2f7;
  color: #3a3a3c;
  font-size: 14px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid #e5e5ea;
`;

const TextInput = styled.input`
  font-family: inherit;
  font-size: 15px;
  color: #1c1c1e;
  background-color: #f9f9fb;
  border: 1px solid #d1d1d6;
  border-radius: 6px;
  padding: 8px 12px;
  box-sizing: border-box;
  width: 100%;
  transition: border-color 0.2s, box-shadow 0.2s, color 0.15s;

  &:focus {
    outline: none;
    border-color: #2a9d8e;
    background-color: #ffffff;
    box-shadow: 0 0 0 2px rgba(42, 157, 142, 0.15);
  }
`;

const TextArea = styled.textarea`
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  color: #1c1c1e;
  background-color: #f9f9fb;
  border: 1px solid #d1d1d6;
  border-radius: 6px;
  padding: 8px 12px;
  box-sizing: border-box;
  width: 100%;
  min-height: 75px;
  resize: vertical;
  transition: border-color 0.2s, box-shadow 0.2s, color 0.15s;

  &:focus {
    outline: none;
    border-color: #2a9d8e;
    background-color: #ffffff;
    box-shadow: 0 0 0 2px rgba(42, 157, 142, 0.15);
  }
`;

const AddFieldSection = styled.div`
  margin-top: 6px;
  padding-top: 10px;
  border-top: 1px dashed #e5e5ea;
`;

const ToggleAddButton = styled.button`
  display: inline-flex;
  align-items: center;
  background: none;
  border: 1px dashed #2a9d8e;
  color: #2a9d8e;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s;

  &:hover {
    background-color: rgba(42, 157, 142, 0.08);
  }
`;

const AddFieldCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: #f9f9fb;
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #e5e5ea;
`;

const AddFieldInputs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const AddInput = styled.input`
  font-family: inherit;
  font-size: 14px;
  padding: 6px 10px;
  border-radius: 4px;
  border: 1px solid #d1d1d6;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #2a9d8e;
  }
`;

const AddActionRow = styled.div`
  display: flex;
  gap: 8px;
`;

const SaveFieldButton = styled.button`
  background-color: #2a9d8e;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: #238276;
  }
`;

const CancelFieldButton = styled.button`
  background-color: #e5e5ea;
  color: #3a3a3c;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background-color: #d1d1d6;
  }
`;
